import { useEffect, useState } from 'react';
import type { FeedbackStatus, FeedbackSeverity, Project } from '@feedback-system/types';
import { fetchProjects } from '../api';

interface ProjectFilterProps {
  projectId?: string;
  status?: string;
  severity?: string;
  onProjectChange: (projectId: string) => void;
  onStatusChange: (status: string) => void;
  onSeverityChange: (severity: string) => void;
}

const STATUSES: FeedbackStatus[] = ['open', 'in_review', 'resolved', 'closed'];
const SEVERITIES: FeedbackSeverity[] = ['low', 'medium', 'high', 'critical'];

export function ProjectFilter({
  projectId,
  status,
  severity,
  onProjectChange,
  onStatusChange,
  onSeverityChange,
}: ProjectFilterProps) {
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    fetchProjects()
      .then(setProjects)
      .catch(() => {
        // Non-fatal: the dropdown just falls back to "All projects" only.
        // The feedback list itself surfaces auth/network errors separately.
      });
  }, []);

  return (
    <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 flex gap-4 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs font-medium text-dark-200 mb-2">Project</label>
        <select
          value={projectId || ''}
          onChange={(e) => onProjectChange(e.target.value)}
          className="select"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs font-medium text-dark-200 mb-2">Status</label>
        <select
          value={status || ''}
          onChange={(e) => onStatusChange(e.target.value)}
          className="select"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === 'in_review' ? 'In Review' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs font-medium text-dark-200 mb-2">Severity</label>
        <select
          value={severity || ''}
          onChange={(e) => onSeverityChange(e.target.value)}
          className="select"
        >
          <option value="">All Severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
